import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.js';
import { onsiteApiFetch } from '../config/api.js';

interface AuditEntry {
  id: string;
  created_at: string;
  timestamp?: string;
  actor_email?: string;
  actor_name?: string;
  user_id?: string;
  action: string;
  action_category?: string;
  entity_type?: string;
  resource_type?: string;
  entity_id?: string;
  resource_id?: string;
  metadata?: Record<string, unknown>;
  details?: Record<string, unknown>;
}

interface AuditResponse {
  entries?: AuditEntry[];
  data?: AuditEntry[];
  total?: number;
  continuation_token?: string;
}

const PAGE_SIZE = 50;

export default function AuditLogPage() {
  const { orgId } = useAuth();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [page, setPage] = useState(0);

  const params = new URLSearchParams();
  params.set('limit', PAGE_SIZE.toString());
  params.set('offset', (page * PAGE_SIZE).toString());
  if (dateFrom) params.set('from', dateFrom);
  if (dateTo) params.set('to', dateTo);
  if (actionFilter) params.set('action', actionFilter);
  if (userFilter) params.set('user', userFilter);

  const { data, isLoading, error } = useQuery({
    queryKey: ['audit-log', orgId, dateFrom, dateTo, actionFilter, userFilter, page],
    queryFn: () => onsiteApiFetch<AuditResponse>(`/api/v1/onsite/audit-log?${params.toString()}`),
    enabled: !!orgId,
  });

  const entries = data?.entries ?? data?.data ?? [];
  const hasNext = entries.length === PAGE_SIZE || !!data?.continuation_token;

  function formatTimestamp(iso?: string): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' });
  }

  function exportCsv() {
    if (entries.length === 0) return;
    const header = ['Timestamp', 'User', 'Action', 'Resource Type', 'Resource ID', 'Details'];
    const rows = entries.map(e => [
      formatTimestamp(e.created_at || e.timestamp),
      e.actor_email || e.actor_name || e.user_id || '(system)',
      e.action,
      e.entity_type || e.resource_type || '',
      e.entity_id || e.resource_id || '',
      JSON.stringify(e.metadata || e.details || {}),
    ]);
    const csv = [header, ...rows].map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().substring(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border bg-card p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-bold text-foreground">
              <ClipboardList className="h-5 w-5 text-primary" />
              Audit Log
            </h1>
            <p className="text-xs text-muted-foreground">Complete record of user and system actions</p>
          </div>
          <button
            onClick={exportCsv}
            disabled={entries.length === 0}
            className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); setPage(0); }}
              className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => { setDateTo(e.target.value); setPage(0); }}
              className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Action</label>
            <input
              type="text"
              placeholder="e.g. job.created"
              value={actionFilter}
              onChange={e => { setActionFilter(e.target.value); setPage(0); }}
              className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">User</label>
            <input
              type="text"
              placeholder="Email or name"
              value={userFilter}
              onChange={e => { setUserFilter(e.target.value); setPage(0); }}
              className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-muted-foreground">
            <ClipboardList className="h-10 w-10" />
            <p className="font-medium text-foreground">Could not load audit log</p>
            <p className="text-sm text-red-400">{(error as Error).message}</p>
          </div>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-muted-foreground">
            <ClipboardList className="h-12 w-12" />
            <p className="text-lg font-medium text-foreground">No audit entries</p>
            <p className="text-sm text-center max-w-xs">
              Adjust filters or check back later as users take actions in the system.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground whitespace-nowrap">Timestamp</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">User</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Action</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell">Resource Type</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden lg:table-cell">Resource ID</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden xl:table-cell">Details</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id} className="border-b border-border last:border-0 hover:bg-secondary/50">
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                    {formatTimestamp(e.created_at || e.timestamp)}
                  </td>
                  <td className="px-4 py-3 text-xs text-foreground">
                    {e.actor_email || e.actor_name || e.user_id || '(system)'}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-primary">{e.action}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground hidden md:table-cell">
                    {e.entity_type || e.resource_type || '—'}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground hidden lg:table-cell max-w-[180px] truncate">
                    {e.entity_id || e.resource_id || '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground hidden xl:table-cell max-w-xs truncate">
                    {Object.keys(e.metadata || e.details || {}).length > 0
                      ? JSON.stringify(e.metadata || e.details)
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="border-t border-border bg-card p-3 flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Page {page + 1} · Showing {entries.length} entries
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4" /> Prev
          </button>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={!hasNext}
            className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50"
          >
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
